'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useSearchParams, useRouter } from 'next/navigation';
import { Map as MapIcon, X, Wifi, Wind, Snowflake, MapPin, Filter, RotateCcw, Flame, Sun, Ship } from 'lucide-react';
import { formatLocation } from '@/lib/location';
import { CruiseCardData } from '@/components/CruiseCard';
import CruiseCard from '@/components/CruiseCard';
import { City } from '@/components/cruises/types';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import dynamic from 'next/dynamic';
import RegionCombobox from './RegionCombobox';
import PriceSlider from './PriceSlider';
import DateRangePicker from './DateRangePicker';

// Dynamically import map component
const SearchResultsMap = dynamic(() => import('./SearchResultsMap'), {
  ssr: false,
  loading: () => (
    <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800">
      <div className="text-slate-500">Loading map...</div>
    </div>
  ),
});

const ALL_EQUIPMENT_ITEMS = [
  { key: 'webasto', labelKey: 'equipment.webastoWithHeating', labelFallback: 'Webasto (Heating)', icon: Flame },
  { key: 'spinnaker', labelKey: 'equipment.spinnaker', labelFallback: 'Spinnaker', icon: Wind },
  { key: 'solar_panels', labelKey: 'equipment.solarPanels', labelFallback: 'Solar Panels', icon: Sun },
  { key: 'dinghy_engine', labelKey: 'equipment.dinghyEngine', labelFallback: 'Dinghy Engine', icon: Ship },
  { key: 'wifi', labelKey: 'equipment.wifi', labelFallback: 'WiFi', icon: Wifi },
  { key: 'air_conditioning', labelKey: 'equipment.airConditioning', labelFallback: 'Air Conditioning', icon: Snowflake },
] as const;

type PriceRangesMap = Record<string, { min: number; max: number }>;

interface SearchResultsClientProps {
  initialCruises: CruiseCardData[];
  initialError?: string | null;
  /** Equipment slugs returned by API meta.equipment_slugs – only these are shown in filters. If empty, all are shown. */
  equipmentSlugs?: string[];
  /** Price min/max per currency from API response (prices.PLN, prices.EUR, etc.). Used for filter range and slider. */
  initialPrices?: PriceRangesMap | null;
}

interface FilterState {
  destination: string | null;
  embarkation_place: City | null;
  disembarkation_place: City | null;
  dateFrom: string | null;
  dateTo: string | null;
  priceMin: number | null;
  priceMax: number | null;
  currency: string; // e.g. PLN, EUR, USD, GBP – from API prices keys
  location: string | null; // Region slug (e.g. Baltic) – use cruise_region/location in API
  /** City filter: "name,country" – API ?place=... returns cruises where this is embarkation OR disembarkation */
  place: string | null;
  cruiseType: string | null;
  equipment: string[];
  yachtType: string | null;
  cruiseTimeMin: number | null;
  cruiseTimeMax: number | null;
}

interface Region {
  id: string;
  name: string;
  slug?: string;
  type?: string;
}

const DEFAULT_CURRENCIES = ['PLN', 'EUR', 'USD'] as const;

function getDefaultPriceRange(currency: string): { min: number; max: number } {
  switch (currency) {
    case 'PLN': return { min: 0, max: 20000 };
    case 'EUR': case 'USD': case 'GBP': return { min: 0, max: 5000 };
    default: return { min: 0, max: 5000 };
  }
}

export default function SearchResultsClient({ initialCruises, initialError, equipmentSlugs = [], initialPrices = null }: SearchResultsClientProps) {
  const t = useTranslations('cruisesPage');
  const tWizard = useTranslations('cruiseWizard');
  const locale = useLocale();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { mergeSubscriptionsFromServer } = useSubscriptions();

  const [cruises, _setCruises] = useState<CruiseCardData[]>(initialCruises);

  useEffect(() => {
    if (!initialCruises?.length) return;
    const items = initialCruises.map((c) => ({
      entity_id: c.main_cruise_id || c.id,
      is_liked: (c as CruiseCardData & { is_liked?: boolean }).is_liked,
    }));
    mergeSubscriptionsFromServer('cruise', items);
  }, [initialCruises, mergeSubscriptionsFromServer]);
  // Keep initial loading deterministic for SSR/CSR hydration.
  // URL-param-driven loading is enabled after mount in effects.
  const hasNoInitialCruises = !initialCruises || initialCruises.length === 0;
  const [loading, setLoading] = useState(hasNoInitialCruises);
  const [error, setError] = useState<string | null>(initialError || null);
  
  // Price ranges from API response (prices.PLN, prices.EUR, etc.) – synced when server returns new data
  const [priceRanges, setPriceRanges] = useState<PriceRangesMap | null>(initialPrices ?? null);
  useEffect(() => {
    if (initialPrices != null) setPriceRanges(initialPrices);
  }, [initialPrices]);

  const [showMap, setShowMap] = useState(false); // Mobile: false = show list, true = show map
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);

  const initialCurrency = (searchParams.get('currency') as string) || 'PLN';
  const initialRange = initialPrices?.[initialCurrency] ?? getDefaultPriceRange(initialCurrency);

  const [filters, setFilters] = useState<FilterState>({
    destination: null,
    embarkation_place: null,
    disembarkation_place: null,
    dateFrom: searchParams.get('embarkation_date_from') || null,
    dateTo: searchParams.get('embarkation_date_to') || null,
    priceMin: searchParams.get('price_min') ? parseFloat(searchParams.get('price_min')!) : initialRange.min,
    priceMax: searchParams.get('price_max') ? parseFloat(searchParams.get('price_max')!) : initialRange.max,
    currency: initialCurrency,
    location: searchParams.get('location') || null, // Use 'location' param for region slug
    place: searchParams.get('place') || null, // City filter: cruises where city is embarkation OR disembarkation
    cruiseType: searchParams.get('cruise_type') || null,
    equipment: (() => {
      const multi = searchParams.getAll('equipment');
      if (multi.length > 0) {
        return multi.map((v) => (v.includes('=') ? v.split('=')[0].trim() : v.trim())).filter(Boolean);
      }
      const single = searchParams.get('equipment');
      if (!single) return [];
      return single.split(',').map((p) => p.split('=')[0].trim()).filter(Boolean);
    })(),
    yachtType: null,
    cruiseTimeMin: searchParams.get('duration_min') ? parseInt(searchParams.get('duration_min')!, 10) : (searchParams.get('cruise_time_min') ? parseInt(searchParams.get('cruise_time_min')!, 10) : null),
    cruiseTimeMax: searchParams.get('duration_max') ? parseInt(searchParams.get('duration_max')!, 10) : (searchParams.get('cruise_time_max') ? parseInt(searchParams.get('cruise_time_max')!, 10) : null),
  });

  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  /** Cities from API for region/place combobox (so city can be shown and found in the list) */
  const [placesForFilter, setPlacesForFilter] = useState<{ name: string; country: string }[]>([]);

  // Embarkation place search
  const [embarkationSearch, setEmbarkationSearch] = useState('');
  const [embarkationOptions, setEmbarkationOptions] = useState<City[]>([]);
  const [loadingEmbarkation, setLoadingEmbarkation] = useState(false);
  const [isEmbarkationFocused, setIsEmbarkationFocused] = useState(false);

  // Disembarkation place search
  const [disembarkationSearch, setDisembarkationSearch] = useState('');
  const [disembarkationOptions, setDisembarkationOptions] = useState<City[]>([]);
  const [loadingDisembarkation, setLoadingDisembarkation] = useState(false);
  const [isDisembarkationFocused, setIsDisembarkationFocused] = useState(false);

  // Currency is now part of filters state
  const currency = filters.currency;
  const setCurrency = (newCurrency: string) => {
    const range = priceRanges?.[newCurrency] ?? getDefaultPriceRange(newCurrency);
    setFilters({
      ...filters,
      currency: newCurrency,
      priceMin: range.min,
      priceMax: range.max,
    });
  };

  // Currencies to show in selector: those present in API prices, or default list
  const availableCurrencies = useMemo(() => {
    if (priceRanges && Object.keys(priceRanges).length > 0) {
      return Object.keys(priceRanges);
    }
    return [...DEFAULT_CURRENCIES];
  }, [priceRanges]);

  // Ref to track when we just clicked a region (to prevent null overwrite)
  const justClickedRegionRef = useRef<string | null>(null);
  /** When user just clicked a city on the map, URL may not have updated yet; prevent location sync from overwriting place */
  const justClickedPlaceRef = useRef<string | null>(null);
  const lastSyncedLocationRef = useRef<string | null>(null);
  const nullClearTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to track current filters state (avoids stale closures in timeouts/callbacks)
  const filtersRef = useRef(filters);
  const priceRangesRef = useRef(priceRanges);
  const hasInitialFetchRef = useRef(false); // Track if initial fetch has been done
  const isFetchingRef = useRef(false); // Track if currently fetching to prevent double calls
  const lastSearchParamsRef = useRef<string>(''); // Track last searchParams to prevent infinite loops

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  useEffect(() => {
    priceRangesRef.current = priceRanges;
  }, [priceRanges]);

  // Sync cruises from server when initialCruises changes (e.g. after applying filters / navigation)
  useEffect(() => {
    _setCruises(initialCruises);
  }, [initialCruises]);

  // Client-side filter by equipment: show only cruises that have ALL selected equipment
  const filteredCruises = useMemo(() => {
    if (!filters.equipment || filters.equipment.length === 0) return cruises;
    return cruises.filter((cruise) => {
      const eq = (cruise as CruiseCardData & { equipment?: Record<string, boolean> }).equipment;
      if (!eq || typeof eq !== 'object') return false;
      return filters.equipment.every((key) => eq[key] === true);
    });
  }, [cruises, filters.equipment]);

  // Available equipment: from API meta.equipment_slugs, or derived from current cruises (only slugs that appear in at least one cruise)
  const availableEquipmentSlugs = useMemo(() => {
    if (equipmentSlugs.length > 0) return equipmentSlugs;
    const set = new Set<string>();
    cruises.forEach((cruise) => {
      const eq = (cruise as CruiseCardData & { equipment?: Record<string, boolean> }).equipment;
      if (eq && typeof eq === 'object') {
        ALL_EQUIPMENT_ITEMS.forEach((item) => {
          if (eq[item.key]) set.add(item.key);
        });
      }
    });
    return Array.from(set);
  }, [equipmentSlugs, cruises]);

  // Fetch regions and places (cities) for destination/region filter
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [regionsRes, placesRes] = await Promise.all([
          fetch(`/api/regions?lang=${locale}`),
          fetch(`/api/cruises/places?lang=${locale}`, { credentials: 'include' }),
        ]);
        if (regionsRes.ok) {
          const regionsData = await regionsRes.json();
          const regionsList = Array.isArray(regionsData) ? regionsData : [];
          setRegions(regionsList);
        }
        if (placesRes.ok) {
          const placesData = await placesRes.json();
          const embarkation = (placesData.embarkation_places || []) as { name: string; country: string }[];
          const middleBreakPorts = (placesData.middle_break_ports || []) as { name: string; country: string }[];
          const disembarkation = (placesData.disembarkation_places || []) as { name: string; country: string }[];
          const byKey = new Map<string, { name: string; country: string }>();
          [...embarkation, ...middleBreakPorts, ...disembarkation].forEach((p) => {
            const key = `${p.name}-${p.country || ''}`;
            if (!byKey.has(key)) byKey.set(key, { name: p.name, country: p.country || '' });
          });
          setPlacesForFilter(Array.from(byKey.values()));
        }
      } catch (err) {
        console.error('Failed to fetch filter data:', err);
      }
    };
    fetchData();
  }, [locale]);

  // MASTER SYNC: Single useEffect to sync URL to state (prevents race conditions)
  useEffect(() => {
    if (regions.length === 0) return; // Wait for regions to load

    const urlPlace = searchParams.get('place');
    const urlLocation = searchParams.get('location');
    const lastSynced = lastSyncedLocationRef.current;
    const justClicked = justClickedRegionRef.current;
    const justClickedPlace = justClickedPlaceRef.current;
    // Cancel any pending null clear timeout
    if (nullClearTimeoutRef.current) {
      clearTimeout(nullClearTimeoutRef.current);
      nullClearTimeoutRef.current = null;
    }

    // Place mode: user just clicked a city, or URL has place= — do NOT restore region (prevents race)
    const activePlace = urlPlace || justClickedPlace;
    if (activePlace) {
      justClickedRegionRef.current = null;
      if (urlPlace === justClickedPlace) {
        justClickedPlaceRef.current = null;
      }
      setFilters(prev => ({
        ...prev,
        place: activePlace,
        location: null,
      }));
      setSelectedRegion(null);
      lastSyncedLocationRef.current = null;
      return;
    }
    justClickedPlaceRef.current = null;

    // Guard: If location is null BUT we just clicked a region, do NOT clear the state
    if (!urlLocation && justClicked) {
      return;
    }

    // Guard: If value is identical to what we last synced, skip (prevents loops)
    if (urlLocation === lastSynced) {
      return;
    }

    // Find region by slug from URL
    const foundRegion = urlLocation ? regions.find((r: Region) => r.slug === urlLocation) : null;

    // Update selectedRegion to match URL (only if different)
    if (foundRegion && foundRegion.slug !== selectedRegion?.slug) {
      setSelectedRegion(foundRegion);

      // Clear justClicked ref ONLY when we successfully sync a non-null value that matches
      if (justClicked === urlLocation) {
        justClickedRegionRef.current = null;
      }
    } else if (!urlLocation && selectedRegion) {
      // DEBOUNCE NULL CLEAR: If URL is null, wait before clearing (router might be transitioning)
      if (justClicked) {
        return; // Don't clear yet
      }

      // Debounce the null clear - wait 200ms and check again
      nullClearTimeoutRef.current = setTimeout(() => {
        const currentUrlLocation = searchParams.get('location');
        const currentJustClicked = justClickedRegionRef.current;

        // Only clear if URL is still null AND we didn't click a region in the meantime
        if (!currentUrlLocation && !currentJustClicked) {
          setSelectedRegion(null);
        }
      }, 200);
      return; // Exit early, let the timeout handle the clear
    }

    // Update filters.location to match URL (only if different to prevent loops)
    if (urlLocation !== filters.location) {
      setFilters(prev => ({
        ...prev,
        location: urlLocation || null,
      }));
    }

    // Update last synced ref
    lastSyncedLocationRef.current = urlLocation;
    
    // Cleanup: Clear justClicked ref when we successfully sync a non-null value
    if (urlLocation && justClicked === urlLocation) {
      justClickedRegionRef.current = null;
    }
    
    // Cleanup function to clear timeout on unmount or dependency change
    return () => {
      if (nullClearTimeoutRef.current) {
        clearTimeout(nullClearTimeoutRef.current);
        nullClearTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, regions]); // Only depend on searchParams and regions

  // Sync pending place click with URL; master sync owns place state from URL.
  useEffect(() => {
    const urlPlace = searchParams.get('place');
    const pending = justClickedPlaceRef.current;
    if (pending) {
      if (urlPlace === pending) {
        justClickedPlaceRef.current = null;
        justClickedRegionRef.current = null;
      }
      return;
    }
  }, [searchParams, filters.place]);

  // Exchange rate logic removed - prices are now sent in selected currency directly

  // Auto-fetch on mount if needed
  useEffect(() => {
    // Skip if we've already done the initial fetch
    if (hasInitialFetchRef.current) {
      return;
    }

    // Check if we have URL params or if initialCruises is empty
    const hasUrlParams = searchParams.toString().length > 0;
    const hasNoInitialCruises = !initialCruises || initialCruises.length === 0;

    // Only fetch if we have URL params or no initial cruises
    if (hasUrlParams || hasNoInitialCruises) {
      hasInitialFetchRef.current = true;
      // Wait a bit for regions to load if needed, then fetch
      const timeoutId = setTimeout(() => {
        if (!isFetchingRef.current) {
          applyFilters();
        }
      }, 200);
      
      return () => clearTimeout(timeoutId);
    } else {
      // If we have initial cruises and no URL params, mark as fetched
      hasInitialFetchRef.current = true;
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Also fetch when searchParams change (user navigates with back/forward) - but only after initial fetch
  useEffect(() => {
    // Only fetch if we've done initial fetch
    if (!hasInitialFetchRef.current) {
      return;
    }

    // Skip if already fetching
    if (isFetchingRef.current) {
      return;
    }

    // Get current searchParams string
    const currentParamsString = searchParams.toString();
    
    // Skip if searchParams haven't actually changed
    if (currentParamsString === lastSearchParamsRef.current) {
      return;
    }
    
    // Update the ref to track this change
    lastSearchParamsRef.current = currentParamsString;

    // Check if searchParams have meaningful changes
    const currentParams = new URLSearchParams(currentParamsString);
    const hasParams = currentParamsString.length > 0;
    
    // Only fetch if params changed and we're not in the middle of applying filters
    if (hasParams) {
      const currentFilters = filtersRef.current;
      const urlCurrency = (currentParams.get('currency') as string) || 'PLN';
      const urlRange = priceRanges?.[urlCurrency] ?? getDefaultPriceRange(urlCurrency);
      const urlPlace = currentParams.get('place') || null;
      const pendingPlace = justClickedPlaceRef.current;
      const newFilters: FilterState = {
        ...currentFilters,
        dateFrom: currentParams.get('embarkation_date_from') || null,
        dateTo: currentParams.get('embarkation_date_to') || null,
        priceMin: currentParams.get('price_min') ? parseFloat(currentParams.get('price_min')!) : urlRange.min,
        priceMax: currentParams.get('price_max') ? parseFloat(currentParams.get('price_max')!) : urlRange.max,
        currency: urlCurrency,
        location: pendingPlace ? null : (currentParams.get('location') || null),
        place: pendingPlace || urlPlace || null,
        cruiseType: currentParams.get('cruise_type') || null,
        cruiseTimeMin: currentParams.get('duration_min') ? parseInt(currentParams.get('duration_min')!, 10) : (currentParams.get('cruise_time_min') ? parseInt(currentParams.get('cruise_time_min')!, 10) : null),
        cruiseTimeMax: currentParams.get('duration_max') ? parseInt(currentParams.get('duration_max')!, 10) : (currentParams.get('cruise_time_max') ? parseInt(currentParams.get('cruise_time_max')!, 10) : null),
        equipment: (() => {
          const multi = currentParams.getAll('equipment');
          if (multi.length > 0) {
            return multi.map((v) => (v.includes('=') ? v.split('=')[0].trim() : v.trim())).filter(Boolean);
          }
          const single = currentParams.get('equipment');
          if (!single) return [];
          return single.split(',').map((p) => p.split('=')[0].trim()).filter(Boolean);
        })(),
      };
      
      // Only update and fetch if filters actually changed
      const filtersChanged = 
        newFilters.dateFrom !== currentFilters.dateFrom ||
        newFilters.dateTo !== currentFilters.dateTo ||
        newFilters.priceMin !== currentFilters.priceMin ||
        newFilters.priceMax !== currentFilters.priceMax ||
        newFilters.currency !== currentFilters.currency ||
        newFilters.location !== currentFilters.location ||
        newFilters.place !== currentFilters.place ||
        newFilters.cruiseType !== currentFilters.cruiseType ||
        newFilters.cruiseTimeMin !== currentFilters.cruiseTimeMin ||
        newFilters.cruiseTimeMax !== currentFilters.cruiseTimeMax ||
        (newFilters.equipment.length !== currentFilters.equipment.length || newFilters.equipment.some((eq, i) => eq !== currentFilters.equipment[i]));
      
      if (filtersChanged) {
        setFilters(newFilters);
        filtersRef.current = newFilters;
        
        // Fetch with updated filters
        setTimeout(() => {
          if (!isFetchingRef.current) {
            applyFilters();
          }
        }, 50);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, priceRanges]); // Re-sync when URL or API price ranges change

  // Fetch places for embarkation/disembarkation
  const fetchPlaces = useCallback(async (query: string, isEmbarkation: boolean) => {
    if (!query || query.length < 2) {
      if (isEmbarkation) {
        setEmbarkationOptions([]);
      } else {
        setDisembarkationOptions([]);
      }
      return;
    }

    if (isEmbarkation) {
      setLoadingEmbarkation(true);
    } else {
      setLoadingDisembarkation(true);
    }

    try {
      const response = await fetch(
        `/api/cruises/places?lang=${locale}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch places');
      }

      const data = await response.json();
      const placesArray = isEmbarkation 
        ? (data.embarkation_places || [])
        : (data.disembarkation_places || []);

      // Filter places by query (case-insensitive)
      const queryLower = query.toLowerCase();
      const filteredPlaces = placesArray.filter((place: { name: string; country: string }) => {
        const nameMatch = place.name?.toLowerCase().includes(queryLower);
        const countryMatch = place.country?.toLowerCase().includes(queryLower);
        return nameMatch || countryMatch;
      });

      // Convert to City format
      const cities: City[] = filteredPlaces.map((place: { name: string; country: string; latitude?: number; longitude?: number }, index: number) => {
        const placeName = place.country 
          ? `${place.name}, ${place.country}`
          : place.name;

        const lat = place.latitude || 0;
        const lng = place.longitude || 0;

        return {
          id: `place-${index}-${place.name}-${place.country}`,
          name: place.name,
          place_name: placeName,
          coordinates: [lng, lat], // [longitude, latitude]
          center: [lng, lat],
          latitude: lat,
          longitude: lng,
          country: place.country || '',
          country_code: '', // Not provided by this endpoint
        };
      });

      if (isEmbarkation) {
        setEmbarkationOptions(cities);
      } else {
        setDisembarkationOptions(cities);
      }
    } catch (error) {
      console.error('Error fetching places:', error);
      if (isEmbarkation) {
        setEmbarkationOptions([]);
      } else {
        setDisembarkationOptions([]);
      }
    } finally {
      if (isEmbarkation) {
        setLoadingEmbarkation(false);
      } else {
        setLoadingDisembarkation(false);
      }
    }
  }, [locale]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (embarkationSearch) {
        fetchPlaces(embarkationSearch, true);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [embarkationSearch, fetchPlaces]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (disembarkationSearch) {
        fetchPlaces(disembarkationSearch, false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [disembarkationSearch, fetchPlaces]);

  const getFullPlaceName = (location?: City | null) =>
    location?.place_name || location?.name || '';


  // Apply filters and fetch cruises - wrapped in useCallback to prevent re-creation
  const applyFilters = useCallback(async () => {
    // Prevent double fetching
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    // Use ref to access latest state even in stale closures (prevents reverting URL)
    const currentFilters = filtersRef.current;

    try {
      const params = new URLSearchParams();
      if (currentFilters.destination) params.set('embarkation_place', currentFilters.destination);
      if (currentFilters.embarkation_place) {
        const placeValue = currentFilters.embarkation_place.country 
          ? `${currentFilters.embarkation_place.name},${currentFilters.embarkation_place.country}`
          : currentFilters.embarkation_place.name;
        params.set('embarkation_place', placeValue);
        params.delete('location');
        params.delete('place');
      }
      if (currentFilters.disembarkation_place) {
        const placeValue = currentFilters.disembarkation_place.country 
          ? `${currentFilters.disembarkation_place.name},${currentFilters.disembarkation_place.country}`
          : currentFilters.disembarkation_place.name;
        params.set('disembarkation_place', placeValue);
        params.delete('location');
        params.delete('place');
      }
      if (currentFilters.place) {
        params.set('place', currentFilters.place);
        params.delete('location');
        params.delete('embarkation_place');
        params.delete('disembarkation_place');
      }
      if (currentFilters.dateFrom) params.set('embarkation_date_from', currentFilters.dateFrom);
      if (currentFilters.dateTo) params.set('embarkation_date_to', currentFilters.dateTo);
      if (currentFilters.priceMin && currentFilters.priceMin > 0) {
        params.set('price_min', currentFilters.priceMin.toString());
      }
      const rangeMax = priceRangesRef.current?.[currentFilters.currency]?.max ?? getDefaultPriceRange(currentFilters.currency).max;
      if (currentFilters.priceMax != null && currentFilters.priceMax < rangeMax) {
        params.set('price_max', currentFilters.priceMax.toString());
      }
      // Always send currency parameter
      params.set('currency', currentFilters.currency);

      // CRITICAL FIX: Use justClickedRegionRef as fallback if filters.location is null
      // This prevents the "stale closure" problem where applyFilters runs with old state
      // and accidentally clears the location we just set in the URL.
      // Also check window.location to see if we have a pending URL update
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const urlLocation = urlParams?.get('location');

      const activeLocation = currentFilters.location || justClickedRegionRef.current || urlLocation;

      if (activeLocation && !currentFilters.place) {
        params.set('location', activeLocation);
      }

      if (currentFilters.cruiseType) params.set('cruise_type', currentFilters.cruiseType);
      if (currentFilters.cruiseTimeMin != null) params.set('duration_min', currentFilters.cruiseTimeMin.toString());
      if (currentFilters.cruiseTimeMax != null) params.set('duration_max', currentFilters.cruiseTimeMax.toString());
      if (currentFilters.equipment.length > 0) {
        params.set('equipment', currentFilters.equipment.map((eq) => `${eq}=true`).join(','));
      }
      params.set('page', '1');
      params.set('page_size', '50');
      params.set('lang', locale);

      // Use URL navigation instead of direct API call - triggers SSR
      const newUrl = `/${locale}/cruises?${params.toString()}`;
      router.push(newUrl);
      
      // Reset loading state - page will re-render with new data from SSR
      isFetchingRef.current = false;
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [locale, router]); // Include locale and router in dependencies

  const clearFilters = useCallback(() => {
    const plnRange = priceRanges?.PLN ?? getDefaultPriceRange('PLN');
    const defaultFilters: FilterState = {
      destination: null,
      embarkation_place: null,
      disembarkation_place: null,
      dateFrom: null,
      dateTo: null,
      priceMin: plnRange.min,
      priceMax: plnRange.max,
      currency: 'PLN',
      location: null,
      place: null,
      cruiseType: null,
      equipment: [],
      yachtType: null,
      cruiseTimeMin: null,
      cruiseTimeMax: null,
    };
    setFilters(defaultFilters);
    filtersRef.current = defaultFilters;
    justClickedRegionRef.current = null;
    justClickedPlaceRef.current = null;
    setEmbarkationSearch('');
    setDisembarkationSearch('');
    setSelectedRegion(null);
    setEmbarkationOptions([]);
    setDisembarkationOptions([]);
    const cleanUrl = `/${locale}/cruises?currency=PLN&page=1&page_size=50&lang=${locale}`;
    router.replace(cleanUrl);
    setShowFiltersDrawer(false);
  }, [locale, router, priceRanges]);

  // Memoize the region click handler to prevent map re-mounting - now uses slug
  const handleRegionClick = useCallback((regionSlug: string, _regionName: string) => {
    if (filters.location === regionSlug) return;

    justClickedRegionRef.current = regionSlug;
    justClickedPlaceRef.current = null;
    const newFiltersState: FilterState = {
      ...filtersRef.current,
      location: regionSlug,
      place: null,
      embarkation_place: null,
      disembarkation_place: null,
    };
    setFilters(newFiltersState);
    filtersRef.current = newFiltersState;

    const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(currentSearch);
    params.set('location', regionSlug);
    params.delete('place');
    params.delete('embarkation_place');
    params.delete('disembarkation_place');
    const queryString = params.toString();
    const newUrl = queryString ? `/cruises?${queryString}` : '/cruises';
    router.replace(newUrl);

    if (regionSlug) {
      setTimeout(() => applyFilters(), 100);
    }
  }, [filters.location, router, applyFilters]);

  // When user clicks a city (place) on the map: filter cruises where that city is embarkation OR disembarkation
  const handlePlaceClick = useCallback((placeName: string, placeCountry: string) => {
    const placeValue = placeCountry ? `${placeName},${placeCountry}` : placeName;
    if (filters.place === placeValue) return;
    justClickedPlaceRef.current = placeValue; // Prevent location sync from overwriting before URL updates
    justClickedRegionRef.current = null;
    const newFiltersState: FilterState = { ...filters, place: placeValue, location: null };
    setFilters(newFiltersState);
    filtersRef.current = newFiltersState;
    setSelectedRegion(null);

    const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(currentSearch);
    params.set('place', placeValue);
    params.delete('location');
    params.delete('embarkation_place');
    params.delete('disembarkation_place');
    const queryString = params.toString();
    router.replace(`/cruises?${queryString}`);
    setTimeout(() => applyFilters(), 50);
  }, [filters, router, applyFilters]);

  const _formatPrice = (price: number | null | undefined, currency: string | null | undefined) => {
    if (!price) return '—';
    return new Intl.NumberFormat(locale === 'pl' ? 'pl-PL' : 'en-US', {
      style: 'currency',
      currency: currency || 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const _formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString(locale === 'pl' ? 'pl-PL' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Get equipment badges from cruise data
  const _getEquipmentBadges = (cruise: CruiseCardData) => {
    const badges: Array<{ icon: typeof Wifi; label: string }> = [];
    const equipment = (cruise as CruiseCardData & { equipment?: { webasto?: boolean; spinnaker?: boolean; solar_panels?: boolean; dinghy_engine?: boolean; wifi?: boolean; air_conditioning?: boolean } }).equipment;
    if (equipment) {
      if (equipment.webasto) badges.push({ icon: Flame, label: tWizard('equipment.webastoWithHeating') || 'Webasto' });
      if (equipment.spinnaker) badges.push({ icon: Wind, label: tWizard('equipment.spinnaker') || 'Spinnaker' });
      if (equipment.solar_panels) badges.push({ icon: Sun, label: tWizard('equipment.solarPanels') || 'Solar Panels' });
      if (equipment.dinghy_engine) badges.push({ icon: Ship, label: tWizard('equipment.dinghyEngine') || 'Dinghy Engine' });
      if (equipment.wifi) badges.push({ icon: Wifi, label: tWizard('equipment.wifi') || 'WiFi' });
      if (equipment.air_conditioning) badges.push({ icon: Snowflake, label: tWizard('equipment.airConditioning') || 'AC' });
    }
    return badges;
  };

  // Count active filters
  const countActiveFilters = (): number => {
    let count = 0;
    if (filters.location) count++;
    if (filters.place) count++;
    if (filters.embarkation_place) count++;
    if (filters.disembarkation_place) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    const range = priceRanges?.[filters.currency] ?? getDefaultPriceRange(filters.currency);
    if (filters.priceMin != null && filters.priceMin > range.min) count++;
    if (filters.priceMax != null && filters.priceMax < range.max) count++;
    if (filters.cruiseType) count++;
    if (filters.equipment.length > 0) count++;
    if (filters.yachtType) count++;
    if (filters.cruiseTimeMin) count++;
    if (filters.cruiseTimeMax) count++;
    return count;
  };

  const activeFiltersCount = countActiveFilters();

  return (
    <main className="fixed inset-0 top-16 w-full h-[calc(100vh-4rem)] overflow-hidden flex flex-col bg-slate-50 dark:bg-slate-950 z-0">
      {/* Mobile Toggle Button */}
      <div className="lg:hidden fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setShowMap(!showMap)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 font-semibold transition-colors"
        >
          {showMap ? (
            <>
              <X size={20} />
              {t('showList') || 'Show List'}
            </>
          ) : (
            <>
              <MapIcon size={20} />
              {t('showMap') || 'Show Map'}
            </>
          )}
        </button>
      </div>

      {/* Desktop Layout: Split View - On mobile only filter bar; list is in Mobile Layout below */}
      <div className="flex-shrink-0 lg:flex-1 flex items-center justify-center w-full overflow-hidden p-4 lg:p-8">
        <div className="w-full max-w-7xl mx-auto h-auto lg:h-[calc(100vh-120px)] rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 shadow-2xl">
          <div className="flex w-full h-full min-h-0 overflow-hidden">
            {/* Left Side: Filter bar + Cruise List on desktop (60%) */}
            <div className="w-full lg:w-[60%] h-full min-h-0 overflow-y-auto border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex flex-col px-6">
              {/* Filter Bar with Glass Effect - Sticky at top */}
              <div className="border-b border-slate-200 dark:border-slate-800/50 p-4 bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-10 shadow-sm">
                {/* Desktop Filter Layout */}
                <div className="hidden lg:grid gap-4">
                  {/* Row 1: Region/Location (Full Width) */}
                  <div>
                    <RegionCombobox
                      regions={regions}
                      selectedRegion={selectedRegion}
                      locationSlug={filters.location}
                      places={placesForFilter}
                      selectedPlace={filters.place}
                      onPlaceSelect={(placeValue) => {
                        if (filters.place === placeValue) return;
                        justClickedPlaceRef.current = placeValue || null;
                        justClickedRegionRef.current = null;
                        const newFiltersState: FilterState = {
                          ...filtersRef.current,
                          place: placeValue || null,
                          location: null,
                          embarkation_place: null,
                          disembarkation_place: null,
                        };
                        setFilters(newFiltersState);
                        filtersRef.current = newFiltersState;
                        setSelectedRegion(null);
                        setEmbarkationSearch('');
                        setDisembarkationSearch('');
                        const currentParams = new URLSearchParams();
                        searchParams.forEach((value, key) => currentParams.set(key, value));
                        if (placeValue) {
                          currentParams.set('place', placeValue);
                          currentParams.delete('location');
                          currentParams.delete('embarkation_place');
                          currentParams.delete('disembarkation_place');
                        } else {
                          currentParams.delete('place');
                        }
                        const queryString = currentParams.toString();
                        router.replace(queryString ? `/cruises?${queryString}` : '/cruises');
                        if (placeValue) setTimeout(() => applyFilters(), 100);
                      }}
                      onSelect={(region) => {
                        if (filters.location === region?.slug) return;
                        if (region?.slug) justClickedRegionRef.current = region.slug;
                        justClickedPlaceRef.current = null;
                        const newFiltersState: FilterState = {
                          ...filtersRef.current,
                          location: region?.slug || null,
                          place: null,
                          embarkation_place: null,
                          disembarkation_place: null,
                        };
                        setFilters(newFiltersState);
                        filtersRef.current = newFiltersState;
                        setEmbarkationSearch('');
                        setDisembarkationSearch('');
                        setEmbarkationOptions([]);
                        setDisembarkationOptions([]);
                        const currentParams = new URLSearchParams();
                        searchParams.forEach((value, key) => currentParams.set(key, value));
                        if (region?.slug) {
                          currentParams.set('location', region.slug);
                          currentParams.delete('place');
                        } else {
                          currentParams.delete('location');
                          justClickedRegionRef.current = null;
                        }
                        currentParams.delete('embarkation_place');
                        currentParams.delete('disembarkation_place');
                        const queryString = currentParams.toString();
                        router.replace(queryString ? `/cruises?${queryString}` : '/cruises');
                        if (region?.slug) setTimeout(() => applyFilters(), 100);
                      }}
                      placeholder={t('filters.cruiseRegion') || 'Select region...'}
                    />
                  </div>

                  {/* Row 2: Embarkation & Disembarkation Ports */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Embarkation Place */}
                    <div className="relative">
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
                        <input
                          type="text"
                          placeholder={t('filters.embarkationPlaceholder') || t('common.search') || 'Search embarkation place...'}
                          value={isEmbarkationFocused
                            ? embarkationSearch
                            : (filters.embarkation_place ? formatLocation(filters.embarkation_place) : embarkationSearch)}
                          onChange={(e) => {
                            setEmbarkationSearch(e.target.value);
                          }}
                          onFocus={() => {
                            setIsEmbarkationFocused(true);
                            if (filters.embarkation_place) {
                              setEmbarkationSearch(getFullPlaceName(filters.embarkation_place));
                            }
                          }}
                          onBlur={() => {
                            setIsEmbarkationFocused(false);
                            if (filters.embarkation_place) {
                              setEmbarkationSearch(formatLocation(filters.embarkation_place));
                            }
                          }}
                          className="w-full h-11 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-lg pl-10 pr-10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {filters.embarkation_place && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters({ ...filters, embarkation_place: null, location: null });
                              setEmbarkationSearch('');
                              setEmbarkationOptions([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-700/50 rounded"
                          >
                            <X className="w-4 h-4 text-slate-400" />
                          </button>
                        )}
                      </div>
                      {loadingEmbarkation && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 p-2">
                          <div className="text-sm text-slate-600 dark:text-slate-400">Loading...</div>
                        </div>
                      )}
                      {isEmbarkationFocused && embarkationOptions.length > 0 && embarkationSearch && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[9999] max-h-60 overflow-y-auto">
                          {embarkationOptions.map((option, index) => (
                            <button
                              key={option.id || `embark-${index}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const portName = option.place_name || option.name;
                                setFilters({ ...filters, embarkation_place: option, location: null });
                                setEmbarkationSearch(portName);
                                setEmbarkationOptions([]);
                                setIsEmbarkationFocused(false);
                                setSelectedRegion(null);
                              }}
                              onMouseDown={(e) => {
                                // Prevent input blur from closing dropdown before click
                                e.preventDefault();
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm text-slate-900 dark:text-white cursor-pointer"
                            >
                              {option.place_name || option.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Disembarkation Place */}
                    <div className="relative">
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
                        <input
                          type="text"
                          placeholder={t('filters.disembarkationPlaceholder') || t('common.search') || 'Search disembarkation place...'}
                          value={isDisembarkationFocused
                            ? disembarkationSearch
                            : (filters.disembarkation_place ? formatLocation(filters.disembarkation_place) : disembarkationSearch)}
                          onChange={(e) => {
                            setDisembarkationSearch(e.target.value);
                          }}
                          onFocus={() => {
                            setIsDisembarkationFocused(true);
                            if (filters.disembarkation_place) {
                              setDisembarkationSearch(getFullPlaceName(filters.disembarkation_place));
                            }
                          }}
                          onBlur={() => {
                            setIsDisembarkationFocused(false);
                            if (filters.disembarkation_place) {
                              setDisembarkationSearch(formatLocation(filters.disembarkation_place));
                            }
                          }}
                          className="w-full h-11 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-lg pl-10 pr-10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {filters.disembarkation_place && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters({ ...filters, disembarkation_place: null, location: null });
                              setDisembarkationSearch('');
                              setDisembarkationOptions([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 dark:hover:bg-slate-700/50 rounded"
                          >
                            <X className="w-4 h-4 text-slate-400" />
                          </button>
                        )}
                      </div>
                      {loadingDisembarkation && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 p-2">
                          <div className="text-sm text-slate-600 dark:text-slate-400">Loading...</div>
                        </div>
                      )}
                      {isDisembarkationFocused && disembarkationOptions.length > 0 && disembarkationSearch && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[9999] max-h-60 overflow-y-auto">
                          {disembarkationOptions.map((option, index) => (
                            <button
                              key={option.id || `disembark-${index}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const portName = option.place_name || option.name;
                                setFilters({ ...filters, disembarkation_place: option, location: null });
                                setDisembarkationSearch(portName);
                                setDisembarkationOptions([]);
                                setIsDisembarkationFocused(false);
                                setSelectedRegion(null);
                              }}
                              onMouseDown={(e) => {
                                // Prevent input blur from closing dropdown before click
                                e.preventDefault();
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm text-slate-900 dark:text-white cursor-pointer"
                            >
                              {option.place_name || option.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 3: Date Range Picker */}
                  <div>
                    <DateRangePicker
                      startDate={filters.dateFrom}
                      endDate={filters.dateTo}
                      onStartDateChange={(date) => setFilters({ ...filters, dateFrom: date })}
                      onEndDateChange={(date) => setFilters({ ...filters, dateTo: date })}
                      placeholder={t('filters.dates') || 'Select date range'}
                    />
                  </div>

                  {/* Row 4: Action Buttons */}
                  <div className="flex items-center gap-3 justify-end">
                    <button
                      onClick={() => setShowFiltersDrawer(true)}
                      className="relative flex items-center gap-2 px-4 py-2 h-11 border border-slate-300 dark:border-slate-700/50 rounded-lg bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors text-sm text-slate-900 dark:text-white"
                    >
                      <Filter className="w-4 h-4" />
                      {t('filters.title') || 'All Filters'}
                      {activeFiltersCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {activeFiltersCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setFilters({
                          destination: null,
                          embarkation_place: null,
                          disembarkation_place: null,
                          dateFrom: null,
                          dateTo: null,
                          priceMin: 0,
                          priceMax: 20000,
                          currency: 'PLN',
                          place: null,
                          location: null,
                          cruiseType: null,
                          equipment: [],
                          yachtType: null,
                          cruiseTimeMin: null,
                          cruiseTimeMax: null,
                        });
                        setSelectedRegion(null);
                        setEmbarkationSearch('');
                        setDisembarkationSearch('');
                        setEmbarkationOptions([]);
                        setDisembarkationOptions([]);
                        window.location.href = '/cruises';
                      }}
                      className="flex items-center gap-2 px-4 py-2 h-11 border border-slate-300 dark:border-slate-700/50 rounded-lg bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors text-sm text-slate-600 dark:text-slate-300"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset
                    </button>
                    <button
                      onClick={applyFilters}
                      disabled={loading}
                      className="px-6 py-2 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {loading ? 'Loading...' : t('filters.apply') || 'Apply Filters'}
                    </button>
                  </div>
                </div>

                {/* Mobile: only "More Filters" button; cruise region is in the filter panel */}
                <div className="lg:hidden">
                  <button
                    onClick={() => setShowFiltersDrawer(true)}
                    className="relative w-full flex items-center justify-center gap-2 px-4 py-3 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <Filter className="w-5 h-5" />
                    More Filters
                    {activeFiltersCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-white text-blue-600 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Cruise List - hidden on mobile; list is shown only in "Mobile Layout" below to avoid duplication */}
              <div className="hidden lg:flex flex-1 flex-col overflow-y-auto pt-6">
            {loading && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-300 dark:border-slate-700 border-t-blue-600 mb-4"></div>
                <p className="text-slate-400 text-sm">Loading cruises...</p>
              </div>
            )}

            {error && (
              <div className="p-4 m-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            {!loading && !error && filteredCruises.length === 0 && (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                <p className="text-lg font-semibold mb-2">
                  {t('cruises.noResults')}
                </p>
                <p className="text-sm">
                  {t('cruises.tryAdjusting')}
                </p>
              </div>
            )}

            {!loading && !error && filteredCruises.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCruises.map((cruise, index) => (
                  <div key={cruise.id || cruise.main_cruise_id || `cruise-${index}`} className="h-full">
                    <CruiseCard cruise={cruise} variant="default" hover={true} />
                  </div>
                ))}
              </div>
            )}
              </div>
            </div>

            {/* Right Side: Map (40%) - Sticky */}
            <div className="hidden lg:block lg:w-[40%] h-full sticky top-0 bg-slate-800 dark:bg-slate-800">
              <div className="w-full h-full">
                <SearchResultsMap
                  key="desktop-map" // Stable key to prevent re-mounting
                  selectedRegionSlug={filters.location}
                  selectedPlace={filters.place}
                  onRegionClick={handleRegionClick}
                  onPlaceClick={handlePlaceClick}
                  selectedEmbarkationPlace={filters.embarkation_place ? {
                    latitude: filters.embarkation_place.latitude || 0,
                    longitude: filters.embarkation_place.longitude || 0
                  } : null}
                  selectedDisembarkationPlace={filters.disembarkation_place ? {
                    latitude: filters.disembarkation_place.latitude || 0,
                    longitude: filters.disembarkation_place.longitude || 0
                  } : null}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout: Toggle between List and Map - only place cruise list shows on mobile */}
      <div className="lg:hidden flex-1 min-h-0 overflow-y-auto">
        {!showMap ? (
          <div className="pt-4 px-4 pb-24">

            {/* Mobile Cruise List */}
            <div className="grid grid-cols-1 gap-6">
              {filteredCruises.map((cruise, index) => (
                <div key={cruise.id || cruise.main_cruise_id || `cruise-${index}`} className="h-full">
                  <CruiseCard cruise={cruise} variant="default" hover={true} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="fixed inset-0 top-16">
            <SearchResultsMap
              key="mobile-map"
              selectedRegionSlug={filters.location}
              selectedPlace={filters.place}
              onRegionClick={handleRegionClick}
              onPlaceClick={handlePlaceClick}
              selectedEmbarkationPlace={filters.embarkation_place ? {
                latitude: filters.embarkation_place.latitude || 0,
                longitude: filters.embarkation_place.longitude || 0
              } : null}
              selectedDisembarkationPlace={filters.disembarkation_place ? {
                latitude: filters.disembarkation_place.latitude || 0,
                longitude: filters.disembarkation_place.longitude || 0
              } : null}
            />
          </div>
        )}
      </div>

      {/* Filters Drawer */}
      {showFiltersDrawer && (
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFiltersDrawer(false)} aria-hidden="true" />
          <div className="absolute right-0 top-0 h-full w-full max-w-md lg:max-w-lg bg-white dark:bg-slate-900 shadow-xl flex flex-col">
            {/* Header: title only; Close and Apply are in bottom panel */}
            <div className="sticky top-0 z-[10000] flex-shrink-0 px-4 py-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {t('filters.title') || 'Filters'}
              </h2>
            </div>
            <div className="p-6 flex-1 overflow-y-auto pb-24 min-h-0">
              <div className="space-y-6">
                {/* Cruise Region - first in drawer (mobile uses this; desktop has it in main bar) */}
                <div className="lg:hidden">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('filters.cruiseRegion') || 'Cruise region'}
                  </label>
                  <RegionCombobox
                    regions={regions}
                    selectedRegion={selectedRegion}
                    locationSlug={filters.location}
                    places={placesForFilter}
                    selectedPlace={filters.place}
                    onPlaceSelect={(placeValue) => {
                      if (filters.place === placeValue) return;
                      justClickedPlaceRef.current = placeValue || null;
                      justClickedRegionRef.current = null;
                      const newFiltersState: FilterState = {
                        ...filtersRef.current,
                        place: placeValue || null,
                        location: null,
                        embarkation_place: null,
                        disembarkation_place: null,
                      };
                      setFilters(newFiltersState);
                      filtersRef.current = newFiltersState;
                      setSelectedRegion(null);
                      setEmbarkationSearch('');
                      setDisembarkationSearch('');
                      const currentParams = new URLSearchParams();
                      searchParams.forEach((value, key) => currentParams.set(key, value));
                      if (placeValue) {
                        currentParams.set('place', placeValue);
                        currentParams.delete('location');
                        currentParams.delete('embarkation_place');
                        currentParams.delete('disembarkation_place');
                      } else currentParams.delete('place');
                      const queryString = currentParams.toString();
                      router.replace(queryString ? `/${locale}/cruises?${queryString}` : `/${locale}/cruises`);
                      if (placeValue) setTimeout(() => applyFilters(), 100);
                    }}
                    onSelect={(region) => {
                      if (filters.location === region?.slug) return;
                      if (region?.slug) justClickedRegionRef.current = region.slug;
                      justClickedPlaceRef.current = null;
                      setSelectedRegion(region || null);
                      const newFiltersState: FilterState = {
                        ...filtersRef.current,
                        location: region?.slug || null,
                        place: null,
                        embarkation_place: null,
                        disembarkation_place: null,
                      };
                      setFilters(newFiltersState);
                      filtersRef.current = newFiltersState;
                      setEmbarkationSearch('');
                      setDisembarkationSearch('');
                      setEmbarkationOptions([]);
                      setDisembarkationOptions([]);
                      const currentParams = new URLSearchParams();
                      searchParams.forEach((value, key) => currentParams.set(key, value));
                      if (region?.slug) {
                        currentParams.set('location', region.slug);
                        currentParams.delete('place');
                      } else {
                        currentParams.delete('location');
                        justClickedRegionRef.current = null;
                      }
                      currentParams.delete('embarkation_place');
                      currentParams.delete('disembarkation_place');
                      const queryString = currentParams.toString();
                      router.replace(queryString ? `/${locale}/cruises?${queryString}` : `/${locale}/cruises`);
                      if (region?.slug) setTimeout(() => applyFilters(), 100);
                    }}
                    placeholder={t('filters.cruiseRegion') || 'Select region...'}
                  />
                </div>

                {/* Embarkation Place */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('filters.embarkationPlace') || 'Embarkation Place'}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                      value={isEmbarkationFocused
                        ? embarkationSearch
                        : (filters.embarkation_place ? formatLocation(filters.embarkation_place) : embarkationSearch)}
                      onChange={(e) => {
                        setEmbarkationSearch(e.target.value);
                      }}
                      onFocus={() => {
                        setIsEmbarkationFocused(true);
                        if (filters.embarkation_place) {
                          setEmbarkationSearch(getFullPlaceName(filters.embarkation_place));
                        }
                      }}
                      onBlur={() => {
                        setIsEmbarkationFocused(false);
                        if (filters.embarkation_place) {
                          setEmbarkationSearch(formatLocation(filters.embarkation_place));
                        }
                      }}
                      placeholder={t('common.search') || 'Type to search...'}
                    />
                    {loadingEmbarkation && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  {isEmbarkationFocused && embarkationOptions.length > 0 && embarkationSearch && (
                    <div className="absolute z-[9999] top-full left-0 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                      {embarkationOptions.map((option, index) => (
                        <div
                          key={option.id || `embark-drawer-${index}`}
                          className="px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer text-slate-900 dark:text-slate-100"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const portName = option.place_name || option.name;
                            setFilters({ ...filters, embarkation_place: option, location: null });
                            setEmbarkationSearch(portName);
                            setEmbarkationOptions([]);
                            setIsEmbarkationFocused(false);
                            setSelectedRegion(null);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          {option.place_name || option.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Disembarkation Place */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('filters.disembarkationPlace') || 'Disembarkation Place'}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                      value={isDisembarkationFocused
                        ? disembarkationSearch
                        : (filters.disembarkation_place ? formatLocation(filters.disembarkation_place) : disembarkationSearch)}
                      onChange={(e) => {
                        setDisembarkationSearch(e.target.value);
                      }}
                      onFocus={() => {
                        setIsDisembarkationFocused(true);
                        if (filters.disembarkation_place) {
                          setDisembarkationSearch(getFullPlaceName(filters.disembarkation_place));
                        }
                      }}
                      onBlur={() => {
                        setIsDisembarkationFocused(false);
                        if (filters.disembarkation_place) {
                          setDisembarkationSearch(formatLocation(filters.disembarkation_place));
                        }
                      }}
                      placeholder={t('common.search') || 'Type to search...'}
                    />
                    {loadingDisembarkation && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  {isDisembarkationFocused && disembarkationOptions.length > 0 && disembarkationSearch && (
                    <div className="absolute z-[9999] top-full left-0 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                      {disembarkationOptions.map((option, index) => (
                        <div
                          key={option.id || `disembark-drawer-${index}`}
                          className="px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer text-slate-900 dark:text-slate-100"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const portName = option.place_name || option.name;
                            setFilters({ ...filters, disembarkation_place: option, location: null });
                            setDisembarkationSearch(portName);
                            setDisembarkationOptions([]);
                            setIsDisembarkationFocused(false);
                            setSelectedRegion(null);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          {option.place_name || option.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Date Range Picker */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('filters.dates') || 'Date Range'}
                  </label>
                  <DateRangePicker
                    startDate={filters.dateFrom}
                    endDate={filters.dateTo}
                    onStartDateChange={(date) => setFilters({ ...filters, dateFrom: date })}
                    onEndDateChange={(date) => setFilters({ ...filters, dateTo: date })}
                    placeholder={t('filters.dates') || 'Select date range'}
                    className="w-full"
                  />
                </div>

                {/* Price Range with Currency Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('filters.price') || 'Price Range'}
                  </label>
                  {/* Currency Selector */}
                  <div className="mb-4 flex gap-2">
                    {availableCurrencies.map((curr) => (
                      <button
                        key={curr}
                        onClick={() => setCurrency(curr)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currency === curr
                            ? 'bg-blue-600 text-white'
                            : 'bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {curr}
                      </button>
                    ))}
                  </div>
                  {/* Price Slider – min/max from API response for selected currency */}
                  <PriceSlider
                    min={priceRanges?.[currency]?.min ?? getDefaultPriceRange(currency).min}
                    max={priceRanges?.[currency]?.max ?? getDefaultPriceRange(currency).max}
                    step={currency === 'PLN' ? 100 : 50}
                    value={[
                      filters.priceMin ?? (priceRanges?.[currency]?.min ?? getDefaultPriceRange(currency).min),
                      filters.priceMax ?? (priceRanges?.[currency]?.max ?? getDefaultPriceRange(currency).max)
                    ]}
                    onChange={(value) => {
                      setFilters({
                        ...filters,
                        priceMin: value[0],
                        priceMax: value[1],
                      });
                    }}
                    currency={currency}
                  />
                </div>

                {/* Cruise Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('filters.cruiseType') || 'Cruise Type'}
                  </label>
                  <select
                    value={filters.cruiseType || ''}
                    onChange={(e) => setFilters({ ...filters, cruiseType: e.target.value || null })}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Types</option>
                    <option value="regatta">Regatta</option>
                    <option value="touristic">Touristic</option>
                    <option value="family">Family</option>
                    <option value="training">Training</option>
                    <option value="delivery">Delivery</option>
                    <option value="expedition">Expedition</option>
                  </select>
                </div>

                {/* Equipment – only options available in current results (API meta.equipment_slugs or derived from cruises) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Equipment
                  </label>
                  <div className="space-y-2">
                    {(availableEquipmentSlugs.length > 0
                      ? ALL_EQUIPMENT_ITEMS.filter((item) => availableEquipmentSlugs.includes(item.key))
                      : ALL_EQUIPMENT_ITEMS
                    ).map((item) => (
                      <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.equipment.includes(item.key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters({ ...filters, equipment: [...filters.equipment, item.key] });
                            } else {
                              setFilters({ ...filters, equipment: filters.equipment.filter((eq) => eq !== item.key) });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <item.icon className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{tWizard(item.labelKey) || item.labelFallback}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('filters.duration') || 'Duration (days)'}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="number"
                      placeholder={t('filters.cruiseTimeMin') || 'Min'}
                      value={filters.cruiseTimeMin || ''}
                      onChange={(e) => setFilters({ ...filters, cruiseTimeMin: e.target.value ? parseInt(e.target.value) : null })}
                      className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      placeholder={t('filters.cruiseTimeMax') || 'Max'}
                      value={filters.cruiseTimeMax || ''}
                      onChange={(e) => setFilters({ ...filters, cruiseTimeMax: e.target.value ? parseInt(e.target.value) : null })}
                      className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

              </div>
            </div>
            
            {/* Bottom panel: Apply, Clear, Close */}
            <div className="sticky bottom-0 left-0 right-0 flex flex-col gap-3 p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    applyFilters();
                    setShowFiltersDrawer(false);
                  }}
                  className="min-h-[48px] px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-base transition-colors touch-manipulation"
                >
                  {loading ? (t('filters.apply') || 'Apply') + '...' : (t('filters.apply') || 'Apply') + ` (${filteredCruises.length})`}
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="min-h-[48px] px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium transition-colors touch-manipulation flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>{t('filters.clear') || 'Clear'}</span>
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowFiltersDrawer(false)}
                className="w-full min-h-[48px] px-6 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium transition-colors touch-manipulation"
              >
                {t('common.close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

